import { useState } from 'react';
import { Box, Card, Code, Collapse, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@/api/audit';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

function Row({ e }: { e: AuditEvent }) {
  const { t } = useTranslation('projects');
  const { relative, dateTime } = useFormat();
  const [open, setOpen] = useState(false);
  const hasPayload = !!e.payload && typeof e.payload === 'object' && Object.keys(e.payload as object).length > 0;
  return (
    <Box py={10} style={{ borderBottom: `1px solid ${brand.border}` }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Group gap={8}>
            <Text fz={14} fw={600}>
              {t(`audit.${e.type}`, { defaultValue: e.type })}
            </Text>
            <Text ff="monospace" fz={11} c={brand.textMuted}>
              {e.type}
            </Text>
          </Group>
          <Text fz={12} c={brand.textMuted}>
            {e.actor ? e.actor.email : t(`audit.actor.${e.actor_kind}`)}
            {e.before || e.after ? ` · ${e.before ?? '∅'} → ${e.after ?? '∅'}` : ''}
            {e.subject_type ? ` · ${e.subject_type} ${e.subject_id}` : ''}
          </Text>
          {e.reason && (
            <Text fz={13} fs="italic">
              „{e.reason}“
            </Text>
          )}
        </Stack>
        <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
          <Text fz={12} c={brand.textMuted} title={dateTime(e.occurred_at)}>
            {relative(e.occurred_at)}
          </Text>
          {hasPayload && (
            <UnstyledButton onClick={() => setOpen((o) => !o)}>
              <Text fz={12} c="forest.6">
                {open ? t('audit.hidePayload') : t('audit.showPayload')}
              </Text>
            </UnstyledButton>
          )}
        </Stack>
      </Group>
      {hasPayload && (
        <Collapse expanded={open}>
          <Code block mt="xs" fz={11}>
            {JSON.stringify(e.payload, null, 2)}
          </Code>
        </Collapse>
      )}
    </Box>
  );
}

/** Audit rows newest first: translated label, raw key, actor, before → after, reason, payload. */
export function AuditTimeline({ events }: { events: AuditEvent[] }) {
  const { t } = useTranslation('projects');
  return (
    <Card>
      {events.length === 0 ? (
        <Text fz={14} c={brand.textMuted}>
          {t('audit.empty')}
        </Text>
      ) : (
        events.map((e) => <Row key={e.public_id} e={e} />)
      )}
    </Card>
  );
}
